import api from "./api";
import {
  CertificadosEmitidosResponse,
  PesquisaTotalizadoresData,
  PesquisaTotalizadoresResponse,
  RespostasPesquisaResponse,
} from "../types/relatoriosTurma";
import { ListAcaoQualificacao, TurmaAcao } from "../types/acao";

const getAuthHeaders = () => {
  const token = sessionStorage.getItem("authToken");

  if (!token) {
    throw new Error("Token nao encontrado");
  }

  return {
    Authorization: `Bearer ${token}`,
  };
};

const postRelatorio = async <TResponse, TPayload extends Record<string, unknown>>(
  url: string,
  payload: TPayload,
): Promise<TResponse> => {
  const response = await api.post<TResponse>(url, payload, {
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json",
    },
  });

  return response.data;
};

type PaginacaoParams = {
  page?: number;
  perPage?: number;
  search?: string;
};

type RelatorioCertificadosEmitidosParams = {
  page?: number;
  perPage?: number;
  acao_id?: number;
  turma_id?: number;
  data_inicio?: string;
  data_fim?: string;
  nome?: string;
  cpf?: string;
  municipio?: number;
};

type RelatorioGeralAcaoParams = {
  acoes_ids?: number[];
  turmas_ids?: number[];
  count_only?: boolean;
  perPage?: number;
};

export type RelatorioGerencialCensoSuasParams = {
  ano: number;
  perPage?: number;
};

export type RelatorioInscritosPorMunicipioParams = {
  data_inicio: string;
  data_fim: string;
  acoes_ids: number[];
  turmas_ids?: number[];
  count_only?: boolean;
  page?: number;
  perPage?: number;
};

export type RelatorioTotalParticipacoesParams = {
  data_inicio: string;
  data_fim: string;
  acoes_ids?: number[];
  turmas_ids?: number[];
  todos_municipios_estado?: boolean;
  municipios_ids?: number[];
  ambitos_ids?: number[];
  lotacoes_ids?: number[];
  orgaos_ids?: number[];
  areas_atuacao_ids?: number[];
  funcoes_ids?: number[];
  vinculos_ids?: number[];
  tipologias_ids?: number[];
  temas_ids?: number[];
  modalidades_ids?: number[];
  idades?: number[];
  cores_racas?: string[];
  possui_deficiencia?: boolean;
  count_only?: boolean;
  page?: number;
  perPage?: number;
};

export type RelatorioConsultaParticipantesParams = {
  data_inicio: string;
  data_fim: string;
  acoes_ids: number[];
  cpf?: string;
  todos_municipios_estado?: boolean;
  municipios_ids?: number[];
  regionais_ids?: number[];
  count_only?: boolean;
  page?: number;
  perPage?: number;
};

export type RelatorioGeralAcaoCountResponse = {
  success: boolean;
  count: number;
};

export type RelatorioGeralAcaoResponse = {
  success: boolean;
  data: any[];
  pagination?: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
};

export type RelatorioGerencialCensoSuasResponse<TData> = {
  success: boolean;
  data: TData;
};

export async function findAcoesRelatorioGeral(
  searchDataInicio: string,
  searchDataFim: string,
): Promise<ListAcaoQualificacao> {
  const response = await api.get<ListAcaoQualificacao>(`/acoes/all`, {
    params: {
      page: 1,
      perPage: 999999,
      searchDataInicio,
      searchDataFim,
    },
    headers: getAuthHeaders(),
  });

  return response.data;
}

export async function findTurmasRelatorioGeral(
  acoesIds: number[],
  dataInicio?: string,
  dataFim?: string,
): Promise<{ turmas: TurmaAcao[]; pagination?: { total: number; per_page: number; current_page: number; last_page: number } }> {
  const response = await api.get(`/turmas/all`, {
    params: {
      perPage: 999999,
      acoes_ids: acoesIds,
      data_inicio: dataInicio || undefined,
      data_fim: dataFim || undefined,
    },
    headers: getAuthHeaders(),
  });

  return response.data;
}

export async function findRelatorioGeralAcaoCount(
  params: RelatorioGeralAcaoParams,
): Promise<RelatorioGeralAcaoCountResponse> {
  return postRelatorio<RelatorioGeralAcaoCountResponse, RelatorioGeralAcaoParams>(`/relatorios/acoes/geral`, {
      ...params,
      count_only: true,
    });
}

export async function findRelatorioGeralAcao(
  params: RelatorioGeralAcaoParams,
): Promise<RelatorioGeralAcaoResponse> {
  return postRelatorio<RelatorioGeralAcaoResponse, RelatorioGeralAcaoParams>(`/relatorios/acoes/geral`, params);
}

export async function findRelatorioTotalParticipacoesCount(
  params: RelatorioTotalParticipacoesParams,
): Promise<RelatorioGeralAcaoCountResponse> {
  return postRelatorio<RelatorioGeralAcaoCountResponse, RelatorioTotalParticipacoesParams>(
    `/relatorios/acoes/total-participacoes`,
    {
      ...params,
      count_only: true,
      page: 1,
      perPage: 999999,
    },
  );
}

export async function findRelatorioTotalParticipacoes(
  params: RelatorioTotalParticipacoesParams,
): Promise<RelatorioGeralAcaoResponse> {
  return postRelatorio<RelatorioGeralAcaoResponse, RelatorioTotalParticipacoesParams>(
    `/relatorios/acoes/total-participacoes`,
    {
      ...params,
      page: 1,
      perPage: 999999,
    },
  );
}

export async function findRelatorioParticipacoesPorMunicipio(
  params: RelatorioTotalParticipacoesParams,
): Promise<RelatorioGeralAcaoResponse> {
  return postRelatorio<RelatorioGeralAcaoResponse, RelatorioTotalParticipacoesParams>(
    `/relatorios/acoes/participacoes-por-municipio`,
    {
      ...params,
      page: 1,
      perPage: 999999,
    },
  );
}

export async function findRelatorioConsultaParticipantesCount(
  params: RelatorioConsultaParticipantesParams,
): Promise<RelatorioGeralAcaoCountResponse> {
  return postRelatorio<RelatorioGeralAcaoCountResponse, RelatorioConsultaParticipantesParams>(
    `/relatorios/acoes/participantes-historico`,
    {
      ...params,
      count_only: true,
      page: 1,
      perPage: 999999,
    },
  );
}

export async function findRelatorioConsultaParticipantes(
  params: RelatorioConsultaParticipantesParams,
): Promise<RelatorioGeralAcaoResponse> {
  return postRelatorio<RelatorioGeralAcaoResponse, RelatorioConsultaParticipantesParams>(
    `/relatorios/acoes/participantes-historico`,
    {
      ...params,
      page: 1,
      perPage: 999999,
    },
  );
}

export async function findRelatorioTotalQualificadosCount(
  params: RelatorioTotalParticipacoesParams,
): Promise<RelatorioGeralAcaoCountResponse> {
  return postRelatorio<RelatorioGeralAcaoCountResponse, RelatorioTotalParticipacoesParams>(
    `/relatorios/acoes/total-qualificados`,
    {
      ...params,
      count_only: true,
      page: 1,
      perPage: 999999,
    },
  );
}

export async function findRelatorioTotalQualificados(
  params: RelatorioTotalParticipacoesParams,
): Promise<RelatorioGeralAcaoResponse> {
  return postRelatorio<RelatorioGeralAcaoResponse, RelatorioTotalParticipacoesParams>(
    `/relatorios/acoes/total-qualificados`,
    {
      ...params,
      page: 1,
      perPage: 999999,
    },
  );
}

export async function findRelatorioMigracaoEducaSuasCount(
  params: RelatorioMigracaoEducaSuasParams,
): Promise<RelatorioGeralAcaoCountResponse> {
  return postRelatorio<RelatorioGeralAcaoCountResponse, RelatorioMigracaoEducaSuasParams>(
    `/relatorios/acoes/migracao-educasuas-mg`,
    {
      ...params,
      count_only: true,
      page: 1,
      perPage: 999999,
    },
  );
}

export async function findRelatorioMigracaoEducaSuas(
  params: RelatorioMigracaoEducaSuasParams,
): Promise<RelatorioGeralAcaoResponse> {
  return postRelatorio<RelatorioGeralAcaoResponse, RelatorioMigracaoEducaSuasParams>(
    `/relatorios/acoes/migracao-educasuas-mg`,
    {
      ...params,
      page: 1,
      perPage: 999999,
    },
  );
}

export async function findRelatorioInscritosPorMunicipioCount(
  params: RelatorioInscritosPorMunicipioParams,
): Promise<RelatorioGeralAcaoCountResponse> {
  return postRelatorio<RelatorioGeralAcaoCountResponse, RelatorioInscritosPorMunicipioParams>(
    `/relatorios/acoes/inscritos-por-municipio`,
    {
      ...params,
      count_only: true,
      page: 1,
      perPage: 999999,
    },
  );
}

export async function findRelatorioInscritosPorMunicipio(
  params: RelatorioInscritosPorMunicipioParams,
): Promise<RelatorioGeralAcaoResponse> {
  return postRelatorio<RelatorioGeralAcaoResponse, RelatorioInscritosPorMunicipioParams>(
    `/relatorios/acoes/inscritos-por-municipio`,
    {
      ...params,
      page: 1,
      perPage: 999999,
    },
  );
}

export async function findRelatorioGerencialBloco1<TData = any>(
  params: RelatorioGerencialCensoSuasParams,
): Promise<RelatorioGerencialCensoSuasResponse<TData>> {
  const response = await api.get<RelatorioGerencialCensoSuasResponse<TData>>(
    `/relatorios/acoes/censo-suas/bloco-1`,
    {
      params: {
        ...params,
        perPage: params.perPage ?? 999999,
      },
      headers: getAuthHeaders(),
    },
  );

  return response.data;
}

export async function findRelatorioGerencialBloco2<TData = any>(
  params: RelatorioGerencialCensoSuasParams,
): Promise<RelatorioGerencialCensoSuasResponse<TData>> {
  const response = await api.get<RelatorioGerencialCensoSuasResponse<TData>>(
    `/relatorios/acoes/censo-suas/bloco-2`,
    {
      params: {
        ...params,
        perPage: params.perPage ?? 999999,
      },
      headers: getAuthHeaders(),
    },
  );

  return response.data;
}

export async function findRelatorioGerencialBloco3<TData = any>(
  params: RelatorioGerencialCensoSuasParams,
): Promise<RelatorioGerencialCensoSuasResponse<TData>> {
  const response = await api.get<RelatorioGerencialCensoSuasResponse<TData>>(
    `/relatorios/acoes/censo-suas/bloco-3`,
    {
      params: {
        ...params,
        perPage: params.perPage ?? 999999,
      },
      headers: getAuthHeaders(),
    },
  );

  return response.data;
}

export async function findRelatorioGerencialBloco4<TData = any>(
  params: RelatorioGerencialCensoSuasParams,
): Promise<RelatorioGerencialCensoSuasResponse<TData>> {
  const response = await api.get<RelatorioGerencialCensoSuasResponse<TData>>(
    `/relatorios/acoes/censo-suas/bloco-4`,
    {
      params: {
        ...params,
        perPage: params.perPage ?? 999999,
      },
      headers: getAuthHeaders(),
    },
  );

  return response.data;
}

export type RelatorioMigracaoEducaSuasParams = {
  acoes_ids: number[];
  turmas_ids?: number[];
  count_only?: boolean;
  page?: number;
  perPage?: number;
};


export async function findRelatorioPesquisaRespostas(
  turmaId: string | number,
  params: PaginacaoParams = {},
): Promise<RespostasPesquisaResponse> {
  const response = await api.get<RespostasPesquisaResponse>(
    `/turmas/${turmaId}/relatorios/pesquisa-respostas`,
    {
      params,
      headers: getAuthHeaders(),
    },
  );

  return response.data;
}

export async function findRelatorioPesquisaTotalizadores(
  turmaId: string | number,
): Promise<PesquisaTotalizadoresData | null> {
  const response = await api.get<PesquisaTotalizadoresResponse>(
    `/turmas/${turmaId}/relatorios/pesquisa-totalizadores`,
    {
      headers: getAuthHeaders(),
    },
  );

  return response.data.data || null;
}

export async function findRelatorioCertificadosEmitidos(
  params: RelatorioCertificadosEmitidosParams = {},
): Promise<CertificadosEmitidosResponse> {
  const response = await api.get<CertificadosEmitidosResponse>(
    `/certificate-templates/relatorios/certificados-emitidos`,
    {
      params,
      headers: getAuthHeaders(),
    },
  );

  return response.data;
}
